import React from 'react';
import { LucideIcon } from 'lucide-react';

interface PageHeaderProps {
  title: string;
  description?: string;
  subtitle?: string; // alias for description
  icon?: LucideIcon;
  actions?: React.ReactNode;
  badge?: {
    label: string;
    variant?: 'default' | 'success' | 'warning' | 'danger';
  };
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  description,
  subtitle,
  icon: Icon,
  actions,
  badge
}) => {
  const badgeStyles = {
    default: 'bg-slate-100 dark:bg-gray-700 text-slate-700 dark:text-slate-300',
    success: 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400',
    warning: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
    danger: 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400'
  };

  return (
    <div className="mb-4 sm:mb-6 md:mb-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="flex items-start gap-3 sm:gap-4 min-w-0 flex-1">
          {Icon && (
            <div className="hidden sm:flex w-10 h-10 md:w-12 md:h-12 rounded-xl md:rounded-2xl bg-gradient-to-br from-[#fff7a8] to-[#fdf751] items-center justify-center shadow-[0_4px_14px_rgba(253,247,81,0.25)] flex-shrink-0">
              <Icon className="w-5 h-5 md:w-6 md:h-6 text-slate-800" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <h1
                className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold text-slate-900 dark:text-slate-100 break-words"
                style={{ letterSpacing: '-0.02em' }}
              >
                {title}
              </h1>
              {badge && (
                <span className={`px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium whitespace-nowrap ${badgeStyles[badge.variant || 'default']}`}>
                  {badge.label}
                </span>
              )}
            </div>
            {(description || subtitle) && (
              <p className="mt-0.5 sm:mt-1 text-xs sm:text-sm text-slate-500 dark:text-slate-400 line-clamp-2 sm:line-clamp-none">
                {description || subtitle}
              </p>
            )}
          </div>
        </div>
        {actions && (
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
};

export default PageHeader;
